import { useState, useEffect } from 'react';
import {
  HYREAD_BASE_URL,
  HYREAD_BOOK_BASE_URL,
  hyreadCodeMapping,
} from '../Constant/constant.js';
import { DISPLAY_RESULT_AMOUNT } from '../Constant/constant.js';

const fetchWithTimeout = (url, options = {}, timeoutMs = 10000) => {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      controller.abort();
      reject(new Error('Request timed out'));
    }, timeoutMs);

    fetch(url, { ...options, signal: controller.signal })
      .then((res) => {
        clearTimeout(id);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(id);
        reject(err);
      });
  });
};

const useHyreadSearch = (cities, searchBookName) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!searchBookName) return;

    setLoading(true);
    setError(null);

    const fetchSearchResults = async () => {
      const selectedCodes = Object.entries(cities)
        .filter(([, selected]) => selected)
        .map(([name]) => hyreadCodeMapping[name])
        .filter(Boolean)
        .join('#');

      if (!selectedCodes) {
        setResults([]);
        setLoading(false);
        setError(null);
        return;
      }

      try {
        const payload = new URLSearchParams({
          keyword: searchBookName,
          searchLib: selectedCodes,
        });

        const headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
          Referer: 'https://one.ebook.hyread.com.tw/',
        };

        const searchRes = await fetchWithTimeout(
          `${HYREAD_BASE_URL}/api/hyread/search`,
          {
            method: 'POST',
            headers,
            body: payload,
          },
          10000
        );

        const searchHtml = await searchRes.text();

        if (searchHtml.includes('您選的圖書館查無結果')) {
          setResults([]);
          setLoading(false);
          return;
        }

        const searchDoc = new DOMParser().parseFromString(
          searchHtml,
          'text/html'
        );
        const anchors = Array.from(
          searchDoc.querySelectorAll("a[href^='/bookDetail.jsp?id=']")
        );
        const ids = [
          ...new Set(
            anchors
              .map((a) => {
                const match = a.getAttribute('href')?.match(/id=(\d+)/);
                return match?.[1] ?? null;
              })
              .filter(Boolean)
          ),
        ].slice(0, DISPLAY_RESULT_AMOUNT);
        const myLibs = selectedCodes.replaceAll('#', ',');
        const detailResults = await Promise.all(
          ids.map(async (id) => {
            try {
              const res = await fetchWithTimeout(
                `${HYREAD_BOOK_BASE_URL}/api/hyread/book?id=${id}&mylibs=${myLibs}`,
                {},
                10000
              );
              const html = await res.text();
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const title =
                doc.querySelector('title')?.textContent?.split('|')[0].trim() ??
                'Unknown title';
              const libraryRows = Array.from(
                doc.querySelectorAll('h3 + table tr')
              ).slice(1);
              const libraries = libraryRows
                .map((row) => {
                  const name = row.querySelector('td a')?.textContent.trim();
                  const link = row
                    .querySelector('td:nth-child(3) a')
                    ?.getAttribute('href');
                  return name && link ? { name, link } : null;
                })
                .filter(Boolean);

              return { title, libraries };
            } catch (e) {
              console.warn('Detail fetch failed for ID:', id, e.message);
              return { title: 'Unknown', libraries: [] };
            }
          })
        );

        setResults(detailResults);
      } catch (err) {
        console.error('Search error:', err);
        setError(err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchBookName, cities, hyreadCodeMapping]);

  return { results, error, loading };
};

export default useHyreadSearch;
